export const useAutoTranslation = () => {
    const client = useSanctumClient();

    /**
     * Auto-translate a JSON field object if translations are missing
     * @param {Object} jsonField - The JSON field object (e.g., {NL: "tekst", FR: "", EN: ""})
     * @param {Array} targetLanguages - Languages to translate to (default: auto-detect)
     * @param {String} editedLanguage - The language that was edited (forces re-translation of other languages)
     * @returns {Object} Updated JSON field object with translations
     */
    const autoTranslateField = async (jsonField, targetLanguages = null, editedLanguage = null) => {
        // Validate input
        if (!jsonField || typeof jsonField !== 'object') {
            return jsonField;
        }

        const allLanguages = ['NL', 'FR', 'EN'];

        // If editedLanguage is specified and it's empty, clear all other languages too
        if (editedLanguage && (!jsonField[editedLanguage] || jsonField[editedLanguage].trim() === '')) {
            allLanguages.forEach(lang => {
                jsonField[lang] = '';
            });
            return jsonField;
        }

        let sourceLanguage;

        // If we know which language was edited, use it as source
        if (editedLanguage && jsonField[editedLanguage] && jsonField[editedLanguage].trim() !== '') {
            sourceLanguage = editedLanguage;
        } else {
            // Find the source language (prioritize non-NL languages first)
            const prioritizedLanguages = ['FR', 'EN', 'NL'];
            sourceLanguage = prioritizedLanguages.find(lang =>
                jsonField[lang] && jsonField[lang].trim() !== ''
            );
        }

        if (!sourceLanguage) {
            return jsonField; // No source text found
        }

        // If targetLanguages not specified, translate to all other languages
        if (!targetLanguages) {
            targetLanguages = allLanguages.filter(lang => lang !== sourceLanguage);
        }

        // If editedLanguage is specified, we force re-translation of all target languages
        // Otherwise, only translate if languages are empty
        const forceTranslation = editedLanguage !== null;

        // Check if we need any translations
        const needsTranslation = forceTranslation || targetLanguages.some(lang =>
            !jsonField[lang] || jsonField[lang].trim() === ''
        );

        if (!needsTranslation) {
            return jsonField; // Already fully translated
        }

        try {
            const response = await client('/api/auto-translate-field', {
                method: 'POST',
                body: {
                    text: jsonField[sourceLanguage],
                    source_language: sourceLanguage,
                    target_languages: targetLanguages
                }
            });

            if (response) {
                targetLanguages.forEach(lang => {
                    // If forced translation (text was edited) or language is empty, update it
                    if ((forceTranslation || !jsonField[lang] || jsonField[lang].trim() === '') && response[lang]) {
                        jsonField[lang] = response[lang];
                    }
                });
            }

            return jsonField;
        } catch (error) {
            console.error('Auto-translation failed:', error);
            return jsonField; // Return original on error
        }
    };

    /**
     * Auto-translate multiple fields of an object
     * @param {Object} object - The object containing JSON fields
     * @param {Array} fields - Array of field names to translate
     * @param {Array} targetLanguages - Languages to translate to (default: auto-detect)
     * @param {String} editedLanguage - The language that was edited (forces re-translation of other languages)
     */
    const autoTranslateObject = async (object, fields, targetLanguages = null, editedLanguage = null) => {
        if (!object || !fields || fields.length === 0) {
            return object;
        }

        for (const field of fields) {
            if (object[field]) {
                object[field] = await autoTranslateField(object[field], targetLanguages, editedLanguage);
            }
        }

        return object;
    };

    /**
     * Auto-translate an array of objects
     * @param {Array} items - Array of objects to translate
     * @param {Array} fields - Array of field names to translate
     * @param {Array} targetLanguages - Languages to translate to (default: auto-detect)
     */
    const autoTranslateArray = async (items, fields, targetLanguages = null) => {
        if (!Array.isArray(items) || items.length === 0) {
            return items;
        }

        for (const item of items) {
            await autoTranslateObject(item, fields, targetLanguages);
        }

        return items;
    };

    return {
        autoTranslateField,
        autoTranslateObject,
        autoTranslateArray
    };
};